import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from 'auth/AuthProvider';
import { CANONICAL_LOCAL_ORIGIN } from 'auth/devOrigin';
import { clearLoggedOutRedirect, isLoggedOutRedirect } from 'auth/tokenStore';
import { destinationAfterLogin, paths, postAuthPath, safeReturnTo } from 'core/routing/paths';
import { useI18n } from 'core/i18n/I18nProvider';
import { ErrorState, Skeleton } from 'shared/components/Feedback';

function BootScreen() {
  const { t } = useI18n();
  return (
    <div className="boot-screen">
      <p>{t('auth.bootstrapping')}</p>
      <Skeleton rows={3} />
    </div>
  );
}

function TemporarySessionScreen() {
  const { t } = useI18n();
  const { sessionMessage, retryBootstrap } = useAuth();
  const title = t(sessionMessage || 'auth.temporarilyUnavailable');
  return (
    <div className="boot-screen">
      <ErrorState
        title={title}
        onRetry={retryBootstrap}
        retryLabel={t('actions.retry')}
        homeAction={false}
      />
      <p className="muted">{t('auth.temporarilyUnavailableBody')}</p>
    </div>
  );
}

function DevOriginNotice() {
  const { t } = useI18n();
  const { showDevOriginNotice } = useAuth();
  if (!showDevOriginNotice) {
    return null;
  }
  return (
    <p className="dev-origin-notice" role="status">
      {t('auth.originMismatch', { origin: CANONICAL_LOCAL_ORIGIN })}
    </p>
  );
}

function anonymousRedirect(location) {
  if (isLoggedOutRedirect()) {
    return paths.welcome;
  }
  const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
  return `${paths.auth}?returnTo=${returnTo}`;
}

function GuardFrame({ children }) {
  return (
    <>
      <DevOriginNotice />
      {children}
    </>
  );
}

export function GuestRoute({ children }) {
  const { status, user } = useAuth();
  const location = useLocation();
  if (status === 'bootstrapping') {
    return <GuardFrame><BootScreen /></GuardFrame>;
  }
  if (status === 'temporarily_unavailable') {
    return <GuardFrame><TemporarySessionScreen /></GuardFrame>;
  }
  if (status === 'authenticated') {
    const params = new URLSearchParams(location.search);
    return <Navigate to={destinationAfterLogin(user, { returnTo: params.get('returnTo') })} replace />;
  }
  clearLoggedOutRedirect();
  return <GuardFrame>{children}</GuardFrame>;
}

export function AuthenticatedRoute({ children }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'bootstrapping') {
    return <GuardFrame><BootScreen /></GuardFrame>;
  }
  if (status === 'temporarily_unavailable') {
    return <GuardFrame><TemporarySessionScreen /></GuardFrame>;
  }
  if (status === 'anonymous') {
    return <Navigate to={anonymousRedirect(location)} replace />;
  }
  if (status === 'forbidden') {
    return <Navigate to={paths.forbidden} replace />;
  }
  return <GuardFrame>{children}</GuardFrame>;
}

export function AppReadyRoute({ children }) {
  const { status, user } = useAuth();
  const location = useLocation();
  if (status === 'bootstrapping') {
    return <GuardFrame><BootScreen /></GuardFrame>;
  }
  if (status === 'temporarily_unavailable') {
    return <GuardFrame><TemporarySessionScreen /></GuardFrame>;
  }
  if (status === 'anonymous') {
    return <Navigate to={anonymousRedirect(location)} replace />;
  }
  if (status === 'forbidden') {
    return <Navigate to={paths.forbidden} replace />;
  }
  const next = postAuthPath(user);
  if (next !== paths.home && next !== location.pathname) {
    return <Navigate to={next} replace />;
  }
  return <GuardFrame>{children}</GuardFrame>;
}

export { BootScreen, TemporarySessionScreen, DevOriginNotice, safeReturnTo };
